/**
 * Members: directory (persisted for offline use), single member, admin member management and
 * another member's photo (editors and admins).
 * Query keys (shared contract): ['members'], ['member', id], ['admin-members'] (+ ['me'] when the
 * changed member is the signed-in user).
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { MeDTO } from '../../shared/dto/auth';
import type {
  AdminMemberDTO,
  AvatarCommitRequest,
  CreateMemberRequest,
  CreateSignupInviteRequest,
  CreateSignupInviteResponse,
  ErrorLogEntryDTO,
  HousekeepingResponse,
  MemberAvatarResponse,
  MemberDTO,
  MemberLinkRequest,
  MemberLinkResponse,
  SignupInviteDTO,
  UpdateMemberRequest,
  UpdateMemberResponse,
} from '../../shared/dto/members';
import { putAvatarParts } from './avatar';
import { api, apiRaw } from './client';

export const MEMBERS_KEY = ['members'] as const;
export const ADMIN_MEMBERS_KEY = ['admin-members'] as const;
export const SIGNUP_INVITES_KEY = ['signup-invites'] as const;
export const memberKey = (id: string) => ['member', id] as const;
/** Owned by AuthProvider (kept literal here so this module stays free of React context imports). */
const ME_KEY = ['me'] as const;

/* ─── Queries ───────────────────────────────────────────────────────────── */

export function fetchMember(id: string, signal?: AbortSignal): Promise<MemberDTO> {
  return api<MemberDTO>(`/members/${encodeURIComponent(id)}`, { signal });
}

export function useMembers(enabled = true) {
  return useQuery({
    queryKey: MEMBERS_KEY,
    queryFn: ({ signal }) => api<MemberDTO[]>('/members', { signal }),
    enabled,
    meta: { persist: true },
    staleTime: 5 * 60_000,
  });
}

export function useMember(id: string | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: memberKey(id ?? ''),
    queryFn: ({ signal }) => fetchMember(id!, signal),
    enabled: !!id,
    meta: { persist: true },
    staleTime: 5 * 60_000,
    // Show the directory entry instantly (and offline) while the single member loads.
    placeholderData: () => qc.getQueryData<MemberDTO[]>(MEMBERS_KEY)?.find((m) => m.id === id),
  });
}

export function useAdminMembers(enabled = true) {
  return useQuery({
    queryKey: ADMIN_MEMBERS_KEY,
    queryFn: ({ signal }) => api<AdminMemberDTO[]>('/admin/members', { signal }),
    enabled,
    staleTime: 15_000,
  });
}

/* ─── Mutations ─────────────────────────────────────────────────────────── */

/** Refresh the member lists, the member and — when it is the signed-in user — ['me'] (profile, badge, role). */
export function invalidateMembers(qc: QueryClient, id?: string): Promise<void> {
  const jobs = [qc.invalidateQueries({ queryKey: MEMBERS_KEY }), qc.invalidateQueries({ queryKey: ADMIN_MEMBERS_KEY })];
  if (id) {
    jobs.push(qc.invalidateQueries({ queryKey: memberKey(id) }));
    if (qc.getQueryData<MeDTO | null>(ME_KEY)?.id === id) jobs.push(qc.invalidateQueries({ queryKey: ME_KEY }));
  }
  return Promise.all(jobs).then(() => undefined);
}

const memberPath = (id: string) => `/admin/members/${encodeURIComponent(id)}`;

export function useCreateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateMemberRequest) => api<unknown>('/admin/members', { method: 'POST', body: req }),
    onSettled: (_d, _e, req) => invalidateMembers(qc, req.id),
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateMemberRequest }) =>
      api<UpdateMemberResponse>(`/members/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
    onSettled: (_d, _e, v) => invalidateMembers(qc, v.id),
  });
}

/** One-time invite or password-reset link (admin). */
export function useMemberLink() {
  return useMutation({
    mutationFn: ({ id, kind }: { id: string } & MemberLinkRequest) =>
      api<MemberLinkResponse>(`${memberPath(id)}/link`, { method: 'POST', body: { kind } satisfies MemberLinkRequest }),
  });
}

/* ─── Invite links by voice (editor/admin) ──────────────────────────────── */

/** Pending invite links by voice (unused, unexpired), newest first. */
export function useSignupInvites(enabled = true) {
  return useQuery({
    queryKey: SIGNUP_INVITES_KEY,
    queryFn: ({ signal }) => api<SignupInviteDTO[]>('/admin/signup-invites', { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useCreateSignupInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateSignupInviteRequest) => api<CreateSignupInviteResponse>('/admin/signup-invites', { method: 'POST', body: req }),
    onSettled: () => qc.invalidateQueries({ queryKey: SIGNUP_INVITES_KEY }),
  });
}

export function useRevokeSignupInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<unknown>(`/admin/signup-invites/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: SIGNUP_INVITES_KEY }),
  });
}

type MemberAction = 'deactivate' | 'reactivate' | 'revoke' | 'erase';

function useMemberAction(action: MemberAction) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<unknown>(`${memberPath(id)}/${action}`, { method: 'POST', body: {} }),
    onSettled: (_d, _e, id) => invalidateMembers(qc, id),
  });
}

export const useDeactivateMember = () => useMemberAction('deactivate');
export const useReactivateMember = () => useMemberAction('reactivate');
export const useRevokeMember = () => useMemberAction('revoke');
export const useEraseMember = () => useMemberAction('erase');

/* ─── Another member's photo (editor/admin) ─────────────────────────────── */

const memberAvatarPath = (id: string) => `/members/${encodeURIComponent(id)}/avatar` as const;

/**
 * Comment boards keep their authors (`?since=` deltas never resend older comments), so the new photo
 * is put on the member's cached comments directly. Returns `list` itself when nothing changes.
 */
export function withAuthorAvatar<C extends { author: { id: string; avatarRev: number } }>(
  list: C[] | undefined,
  userId: string,
  rev: number,
): C[] | undefined {
  if (!list?.some((c) => c.author.id === userId && c.author.avatarRev !== rev)) return list;
  return list.map((c) => (c.author.id === userId && c.author.avatarRev !== rev ? { ...c, author: { ...c.author, avatarRev: rev } } : c));
}

/**
 * Everywhere the member's photo shows: directory, profile, admin list and ['me'] (invalidateMembers),
 * comment boards (patched), event rosters and attendance statistics (refetched). Keys are literal here
 * (['event-comments', id], ['event-roster', id] in api/calendar.ts; ['stats-*'] in api/stats.ts) so
 * this module does not load the calendar outbox.
 */
export function showMemberAvatar(qc: QueryClient, userId: string, rev: number): Promise<void> {
  qc.setQueriesData<{ author: { id: string; avatarRev: number } }[]>({ queryKey: ['event-comments'] }, (list) => withAuthorAvatar(list, userId, rev));
  return Promise.all([
    invalidateMembers(qc, userId),
    qc.invalidateQueries({ queryKey: ['event-roster'] }),
    qc.invalidateQueries({ queryKey: ['stats-attendance'] }),
    qc.invalidateQueries({ queryKey: ['stats-member', userId] }),
  ]).then(() => undefined);
}

/**
 * Upload and commit a new photo of `member` (512 + 128 px JPEG). The first guess is avatarRev + 1;
 * a 409 stale_avatar_rev (e.g. after a removal) is retried once with the server's revision.
 */
export async function uploadMemberAvatar(member: { id: string; avatarRev: number }, blob512: Blob, blob128: Blob): Promise<MemberAvatarResponse> {
  const base = memberAvatarPath(member.id);
  const rev = await putAvatarParts(
    base,
    member.avatarRev + 1,
    blob512,
    blob128,
    async () => (await api<MemberDTO>(`/members/${encodeURIComponent(member.id)}`)).avatarRev + 1,
  );
  const body: AvatarCommitRequest = { rev };
  return api<MemberAvatarResponse>(`${base}/commit`, { method: 'POST', body });
}

/** Remove the photo of `id` (initials are shown instead). */
export function removeMemberAvatar(id: string): Promise<MemberAvatarResponse> {
  return api<MemberAvatarResponse>(memberAvatarPath(id), { method: 'DELETE' });
}

/**
 * Puts `rev` on the member's cached profile and list rows at once, so the profile and the admin sheet
 * show the change even when the refetch that follows fails (weak wifi, offline). Only avatarRev is
 * copied: the admin response carries fields that do not belong in the persisted ['member', id].
 */
export function setCachedAvatarRev(qc: QueryClient, userId: string, rev: number): void {
  const patch = <M extends { id: string; avatarRev: number }>(m: M): M => (m.id === userId && m.avatarRev !== rev ? { ...m, avatarRev: rev } : m);
  qc.setQueryData<MemberDTO>(memberKey(userId), (m) => (m ? patch(m) : m));
  qc.setQueryData<MemberDTO[]>(MEMBERS_KEY, (list) => list?.map(patch));
  qc.setQueryData<AdminMemberDTO[]>(ADMIN_MEMBERS_KEY, (list) => list?.map(patch));
}

export interface MemberAvatarVars {
  member: { id: string; avatarRev: number };
  /** null = remove the photo. */
  blobs: { big: Blob; small: Blob } | null;
}

/**
 * Options of useMemberAvatar (a plain function so tests can run them without React).
 * The refreshes are NOT awaited: TanStack awaits a promise returned from onSuccess/onError before
 * mutateAsync settles, and an offline refetch pauses until the connection returns — that would keep the
 * crop sheet locked (Save spinning, Cancel disabled) with no error shown.
 */
export function memberAvatarMutation(qc: QueryClient) {
  return {
    mutationFn: ({ member, blobs }: MemberAvatarVars): Promise<MemberAvatarResponse> =>
      blobs ? uploadMemberAvatar(member, blobs.big, blobs.small) : removeMemberAvatar(member.id),
    onSuccess: (res: MemberAvatarResponse, v: MemberAvatarVars) => {
      setCachedAvatarRev(qc, v.member.id, res.avatarRev);
      void showMemberAvatar(qc, v.member.id, res.avatarRev);
    },
    onError: (_e: unknown, v: MemberAvatarVars) => {
      void invalidateMembers(qc, v.member.id);
    },
  };
}

/** Add / replace (`blobs`) or remove (`blobs` null) another member's photo; refreshes every place that shows it. */
export function useMemberAvatar() {
  const qc = useQueryClient();
  return useMutation(memberAvatarMutation(qc));
}

/* ─── Error log & housekeeping (admin) ──────────────────────────────────── */

export const ADMIN_ERRORS_KEY = ['admin-errors'] as const;

export function useAdminErrors(enabled = true) {
  return useQuery({
    queryKey: ADMIN_ERRORS_KEY,
    queryFn: ({ signal }) => api<ErrorLogEntryDTO[]>('/admin/errors', { signal }),
    enabled,
    staleTime: 10_000,
  });
}

export function useHousekeeping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<HousekeepingResponse>('/admin/housekeeping', { method: 'POST', body: {}, timeoutMs: 30_000 }),
    onSettled: () => qc.invalidateQueries({ queryKey: ADMIN_ERRORS_KEY }),
  });
}

/* ─── Export ────────────────────────────────────────────────────────────── */

/** GET /api/admin/members/:id/export → JSON attachment, saved as a file in the browser. */
export async function downloadMemberExport(id: string, fallbackName: string): Promise<void> {
  const res = await apiRaw(`${memberPath(id)}/export`, { timeoutMs: 30_000 });
  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get('content-disposition')) ?? fallbackName;
  saveBlob(blob, name);
}

/** RFC 6266: prefer filename* (UTF-8), fall back to the plain filename. */
export function filenameFromDisposition(h: string | null): string | null {
  if (!h) return null;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(h);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* malformed – try the plain form */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(h);
  return plain?.[1]?.trim() || null;
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
